/// <amd-module name="Dashboards/Grid/DashboardGridItem" />
import * as React from "react";

export interface GridItemPosition {
    id: string;
    row: number;
    rowSpan: number;
    column: number;
    columnSpan: number;
}

export interface GridItemProps extends GridItemPosition {
    /** Indicates if this is the focal element, for special treatment in the grid (high z-index). */
    isFocalPreviewElement?: boolean;

    /** Drag event if item is being dropped from outside the grid */
    dragEvent?: Event;
}

/**
 * A container of a visualization rendered on a dashboard grid. Requires consumers of
 * the grid to explicitly define which cells of the grid the item spans across.
 */
export const GridItem = React.forwardRef<HTMLDivElement, { children?: React.ReactNode } & GridItemProps>((props, ref) => (
    <GridItemImplementation forwardRef={ref} {...props} />
));

interface GridItemImplementationProps extends GridItemProps {
    /** Allows consumers of GridItem to provide a ref that is forwarded to the outermost element of the item. */
    forwardRef: React.Ref<HTMLDivElement> | undefined;
}

export interface GridItemElement extends React.ReactElement<Readonly<GridItemProps & { "data-grid"?: ReactGridLayout.Layout }>> {}

/**
 * A container of a visualization rendered on a dashboard grid. Requires consumers of
 * the grid to explicitly define which cells of the grid the item spans across.
 */
class GridItemImplementation extends React.Component<GridItemImplementationProps> {
    public render(): JSX.Element | null {
        const { row, rowSpan, column, columnSpan, forwardRef, children, id, isFocalPreviewElement, dragEvent, ...otherProps } = this.props;
        const conditionalClassName = isFocalPreviewElement ? "focal-grid-item" : "";
        return (
            <div
                {...otherProps} // To support unknown props provided by react-grid-layout
                ref={forwardRef}
                className={`grid-item ${(otherProps as any).className} ${conditionalClassName}`}
                style={{ ...this.generateGridItemStyles(), ...(otherProps as any).style }}
            >
                {children}
            </div>
        );
    }

    private generateGridItemStyles(): React.CSSProperties {
        const { row, column, rowSpan, columnSpan } = this.props;

        // IE 11 compatibility
        // The calculations take into account the gutters
        const msGridStyles: any = {
            msGridColumn: `${column * 2 - 1}`,
            msGridColumnSpan: `${columnSpan * 2 - 1}`,
            msGridRow: `${row * 2 - 1}`,
            msGridRowSpan: `${rowSpan * 2 - 1}`
        };

        return {
            ...msGridStyles,
            gridColumnStart: column,
            gridColumnEnd: `span ${columnSpan}`,
            gridRowStart: row,
            gridRowEnd: `span ${rowSpan}`
        };
    }
}
